import React, { FC, useCallback, useContext, useEffect } from 'react';
import { gapi } from 'gapi-script';
import { useMutation } from 'react-apollo';
import { useTranslation } from 'react-i18next';
import { GoogleLogin } from 'react-google-login';

import Button from 'vibo-ui/Button';
import { SignInContext } from 'components/context/SignInContext';

import { ReactComponent as GoogleLogoLetterIcon } from 'resources/img/svg/googleLogoLetter.svg';

import { onError } from 'graphql/helpers';
import { DEFAULT_LAST_NAME } from './constants';

import { COMPLETE_SIGN_UP, SIGN_GOOGLE } from 'graphql/mutations/auth';

import { GA4LoginSource } from 'components/context/GA4Context/interfaces';
import { UserAction } from 'types/enums';

import useStyles from './style';

const GoogleLoginButton: FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  const clientId = process.env.REACT_APP_GOOGLE_CLIENTID!;

  const { handleLogUserIn } = useContext(SignInContext);

  const [completeSignup] = useMutation<CompleteSignupResponse>(COMPLETE_SIGN_UP, {
    onCompleted: data => handleGoogleLogIn(data.completeSignup),
    onError,
  });

  const [signGoogle] = useMutation<SignGoogleResponse & AuthResponse>(SIGN_GOOGLE, {
    onCompleted: data => {
      if (data.signGoogle.action === UserAction.COMPLETE_SIGN_UP) {
        const signUpData = data.signGoogle.data;

        completeSignup({
          variables: {
            payload: { ...signUpData, lastName: signUpData.lastName || DEFAULT_LAST_NAME },
            token: data.signGoogle.token,
          },
        });
      } else {
        handleGoogleLogIn(data.signGoogle);
      }
    },
    onError,
  });

  const handleGoogleLogIn = useCallback((variables: SocialSignInResponse) => {
    handleLogUserIn(
      {
        variables,
      },
      GA4LoginSource.google
    );
  }, []);

  const responseGoogle = useCallback(response => {
    signGoogle({ variables: { token: response.tokenId, clientId } });
  }, []);

  return (
    // @ts-ignore
    <GoogleLogin
      render={btnProps => (
        <Button
          prefixIcon={GoogleLogoLetterIcon}
          className={classes.googleLoginButton}
          displayType="primary"
          shape="round"
          size="lg"
          {...btnProps}
        >
          {t('signInGoogle')}
        </Button>
      )}
      clientId={clientId}
      buttonText="Login"
      onSuccess={responseGoogle}
      cookiePolicy={'single_host_origin'}
    />
  );
};

const GoogleLoginButtonWrapper = () => {
  const clientId = process.env.REACT_APP_GOOGLE_CLIENTID!;

  useEffect(() => {
    function start() {
      gapi.client.init({
        clientId,
        scope: '',
      });
    }

    gapi.load('client:auth2', start);
  }, []);

  return <GoogleLoginButton />;
};

export default GoogleLoginButtonWrapper;
